import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Button, Radio } from 'antd'

import { ArrowRightOutlined } from '@ant-design/icons'
import { intersection, uniq } from 'lodash'

import { selectShapeData } from '@store/slices/shapes/selectors'

import { useLocalStorage } from '@/hooks'

import DataTypeSelect from './DataTypeSelect'

type Selections = [number[], number[]] | []

type Props = {
  children: (props: {
    disabledRows: number[]
    selectionStrategy: 'column' | 'row' | 'freeform' | 'group'
    selections: Selections
    sendSelections: (value: Selections) => void
  }) => React.ReactNode
  hasData: boolean
  handleSelectData: (
    activeRowTags: string[],
    selectionStrategy: string,
    selections: Selections
  ) => void
  rowMetaTags: string[]
  selectionsFor: (strategy: string) => Selections
  setSelections: (value: Selections, strategy: string) => void
  tableId: number
  storedStrategy?: string
  rowNames: string[]
  columnNames: string[]
  isSavedTable: boolean
}

const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

const summarizeNames = (names: string[]) =>
  names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ')

const InputTableWrapper = ({
  children,
  hasData,
  handleSelectData,
  rowMetaTags,
  selectionsFor,
  setSelections,
  tableId,
  storedStrategy,
  rowNames,
  columnNames,
  isSavedTable
}: Props) => {
  const { dataSelection } = useSelector(selectShapeData)
  const uniqueRowTags: string[] = useMemo(
    () => Array.from(new Set(rowMetaTags || [])),
    [rowMetaTags]
  )
  const [selectionStrategy, setSelectionStrategy] = useLocalStorage<
    'column' | 'row' | 'freeform' | 'group'
  >('selectionStrategy', 'column')
  const [activeRowTags, setActiveRowTags] = useLocalStorage<string[]>('rowTags', [])

  const disabledRows = useMemo(() => {
    return (
      rowMetaTags
        ?.map((el, idx) => {
          return activeRowTags.includes(el) ? null : idx
        })
        ?.filter(el => el !== null) || []
    )
  }, [activeRowTags, rowMetaTags])

  useEffect(() => {
    if (isSavedTable && dataSelection?.rowTags) {
      setActiveRowTags(dataSelection.rowTags)
    } else if (
      uniqueRowTags.length > 0 &&
      intersection(uniqueRowTags, activeRowTags).length === 0
    ) {
      setActiveRowTags(uniqueRowTags)
    }

    if (isSavedTable && dataSelection?.selectionStrategy && !storedStrategy) {
      setSelectionStrategy(
        {
          by_column_position: 'column',
          by_name: 'column',
          by_absolute_position: 'freeform',
          by_parent_group: 'group',
          by_group_inclusion: 'group',
          by_group_name: 'group'
        }[dataSelection.selectionStrategy]
      )
    }
  }, [dataSelection, uniqueRowTags, isSavedTable])

  useEffect(() => {
    if (storedStrategy) {
      setSelectionStrategy(storedStrategy as typeof selectionStrategy)
    }
  }, [tableId, storedStrategy])

  const selectBtnDisabled = !hasData || activeRowTags.length === 0

  const selections = selectionsFor(selectionStrategy)

  const childrenProps = {
    disabledRows,
    selectionStrategy,
    selections,
    sendSelections: (value: Selections) => setSelections(value, selectionStrategy)
  }

  const [selectedRows = [], selectedColumns = []] = selections
  const selectedNames = uniq(
    selectionStrategy === 'row'
      ? selectedRows.map(idx => rowNames[idx])
      : selectedColumns.map(idx => columnNames[idx])
  ).filter(Boolean)
  const activeTypeCount = intersection(uniqueRowTags, activeRowTags).length

  return (
    <section className="workbench-card input-card">
      <div className="card-head">
        <h3>Input table</h3>
        {hasData && (
          <span className="muted">
            {pluralize(rowNames.length, 'row')} · {pluralize(columnNames.length, 'column')}
          </span>
        )}
      </div>

      {hasData && (
        <div className="card-toolbar">
          <div className="toolbar-field select-by">
            <span className="toolbar-label">Select by</span>
            <Radio.Group
              aria-label="Select by"
              className="segmented"
              optionType="button"
              value={selectionStrategy}
              onChange={e => setSelectionStrategy(e.target.value)}
              options={[
                { label: 'Column', value: 'column' },
                { label: 'Row', value: 'row' },
                { label: 'Freeform', value: 'freeform' },
                { label: 'Group', value: 'group' }
              ]}
            />
          </div>
          {uniqueRowTags.length > 0 && (
            <div className="toolbar-field data-type">
              <span className="toolbar-label">Data type</span>
              <DataTypeSelect
                rowMetaTags={rowMetaTags}
                value={activeRowTags}
                onChange={setActiveRowTags}
              />
            </div>
          )}
        </div>
      )}

      <div className="grid-area">
        {hasData ? (
          children(childrenProps)
        ) : (
          <div className="empty-state">Pick a table from the list on the left.</div>
        )}
      </div>

      <div className="card-foot">
        {hasData ? (
          <span className="selection-summary">
            {selectedNames.length > 0 ? (
              <>
                <span className="dot" />
                <span className="summary-text">{summarizeNames(selectedNames)}</span>
                <span className="sep">·</span>
                {pluralize(selectedRows.length, 'row')}
              </>
            ) : (
              'Nothing selected yet'
            )}
            {uniqueRowTags.length > 0 && (
              <>
                <span className="sep">·</span>
                {activeTypeCount} of {uniqueRowTags.length} data types
              </>
            )}
          </span>
        ) : (
          <span />
        )}
        <Button
          disabled={selectBtnDisabled}
          onClick={() => {
            handleSelectData(activeRowTags, selectionStrategy, selections)
          }}
        >
          Select
          <ArrowRightOutlined />
        </Button>
      </div>
    </section>
  )
}

export default InputTableWrapper
